import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, ShieldCheck, CreditCard, AlertCircle } from "lucide-react";
import { API } from "@/lib/api";

const SDK_URLS = {
  production: "https://web.squarecdn.com/v1/square.js",
  sandbox: "https://sandbox.web.squarecdn.com/v1/square.js",
};

function loadSquareSdk(url) {
  return new Promise((resolve, reject) => {
    if (window.Square) return resolve(window.Square);
    const existing = document.querySelector(`script[src="${url}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Square));
      existing.addEventListener("error", () => reject(new Error("Square SDK failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.src = url;
    s.async = true;
    s.onload = () => resolve(window.Square);
    s.onerror = () => reject(new Error("Square SDK failed to load"));
    document.head.appendChild(s);
  });
}

// amountCents: authoritative amount (display only — the server recomputes & charges).
export const SquarePayment = ({ reference, amountCents, onSuccess, payEndpoint = "/payments/service-order", payLabel }) => {
  const cardRef = useRef(null);
  const containerRef = useRef(null);
  const [status, setStatus] = useState("init"); // init | ready | processing | error
  const [error, setError] = useState("");
  const amountLabel = `$${((amountCents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: cfg } = await axios.get(`${API}/payments/config`);
        if (!cfg.configured) throw new Error("Payments are not configured yet.");
        const sdkUrl = SDK_URLS[cfg.environment === "production" ? "production" : "sandbox"];
        const Square = await loadSquareSdk(sdkUrl);
        if (cancelled) return;
        const payments = Square.payments(cfg.application_id, cfg.location_id);
        const card = await payments.card();
        await card.attach(containerRef.current);
        if (cancelled) return;
        cardRef.current = card;
        setStatus("ready");
      } catch (e) {
        if (!cancelled) { setError(e.message || "Unable to start checkout."); setStatus("error"); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const pay = async () => {
    if (!cardRef.current || status === "processing") return;
    setStatus("processing");
    setError("");
    try {
      const result = await cardRef.current.tokenize({
        amount: String((amountCents || 0) / 100),
        currencyCode: "USD",
        intent: "CHARGE",
        customerInitiated: true,
        sellerKeyedIn: false,
        billingContact: { countryCode: "US" },
      });
      if (result.status !== "OK") {
        throw new Error("Please check your card details and try again.");
      }
      const { data } = await axios.post(`${API}${payEndpoint}`, {
        reference,
        source_id: result.token,
      });
      onSuccess(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Payment failed. Please try again.";
      setError(msg);
      setStatus("ready");
    }
  };

  return (
    <div data-testid="square-payment" className="rounded-3xl holo-border p-6 sm:p-8">
      <div className="flex items-center justify-between mb-1">
        <p className="eyebrow">Secure Checkout</p>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-tech tracking-wide text-[color:var(--chrome-3)]">
          <ShieldCheck size={14} className="text-[color:var(--cyan)]" /> Securely processed by Square
        </span>
      </div>
      <div className="flex items-baseline justify-between mb-5">
        <p className="text-sm text-[color:var(--chrome-2)]">Order {reference}</p>
        <p data-testid="pay-amount" className="font-display font-black text-3xl text-holo">{amountLabel}</p>
      </div>

      {status === "init" && (
        <div className="flex items-center gap-2 text-sm text-[color:var(--chrome-3)] py-6">
          <Loader2 size={16} className="animate-spin" /> Loading secure card form…
        </div>
      )}

      <div ref={containerRef} data-testid="square-card-container"
        className={`square-card-field ${status === "error" ? "hidden" : ""}`} />

      {error && (
        <div data-testid="pay-error" className="mt-4 flex items-start gap-2 rounded-xl border border-[color:var(--magenta)]/40 bg-[color:var(--magenta)]/5 p-3 text-sm text-[color:var(--chrome-1)]">
          <AlertCircle size={16} className="text-[color:var(--magenta)] shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {status !== "error" && (
        <button data-testid="pay-button" onClick={pay} disabled={status !== "ready"}
          className="btn-holo w-full rounded-xl py-4 text-sm sm:text-base mt-5 inline-flex items-center justify-center gap-2 disabled:opacity-50">
          {status === "processing"
            ? (<><Loader2 size={18} className="animate-spin" /> PROCESSING…</>)
            : (<><CreditCard size={18} /> PAY {amountLabel}</>)}
        </button>
      )}

      <p className="text-[11px] text-[color:var(--chrome-3)] mt-3 text-center">
        We never see or store your card number. Payment is processed securely by Square.
      </p>
    </div>
  );
};
